import React, { useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

const DEFAULT_TITLE =
  "Square Trigger | Digital Agency Vancouver - Website Development & SEO";

const DEFAULT_DESCRIPTION =
  "Square Trigger is a Vancouver-based digital agency specializing in website development and SEO to drive measurable growth across Vancouver and Canada.";

const CANONICAL_ORIGIN = "https://squaretrigger.com";
const DEFAULT_OG_IMAGE = `${CANONICAL_ORIGIN}/logo3.webp`;

const SEO = ({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  keywords,
  ogTitle,
  ogDescription,
  ogSiteName = "Square Trigger",
  ogImage = DEFAULT_OG_IMAGE,
  ogType = "website",
  noIndex = false,
}) => {
  const location = useLocation();

  const canonicalUrl = useMemo(() => {
    let normalizedPath = location.pathname === "/" ? "" : location.pathname;

    if (normalizedPath === "/blog") {
  normalizedPath = "/blogs";
} else if (normalizedPath.startsWith("/blog/")) {
  normalizedPath = normalizedPath.replace("/blog/", "/blogs/");
} else if (normalizedPath === "/company/about") {
  normalizedPath = "/about";
} else if (
  normalizedPath === "/resources/faqs" ||
  normalizedPath === "/resources-faqs"
) {
  normalizedPath = "/faqs";
} else if (normalizedPath === "/digital-agency-surrey") {
  normalizedPath = "/digital-marketing-agency-surrey";
} else if (normalizedPath === "/digital-agency-burnaby") {
  normalizedPath = "/digital-marketing-agency-burnaby";
} else if (normalizedPath === "/digital-agency-richmond") {
  normalizedPath = "/digital-marketing-agency-richmond";
} else if (normalizedPath === "/digital-agency-north-vancouver") {
  normalizedPath = "/top-digital-agency-in-north-vancouver";
} else if (normalizedPath === "/digital-agency-coquitlam") {
  normalizedPath = "/digital-marketing-agency-coquitlam";
} else if (normalizedPath === "/digital-agency-langley") {
  normalizedPath = "/digital-marketing-agency-langley";
}

    return `${CANONICAL_ORIGIN}${normalizedPath}`.replace(/\/+$/, "");
  }, [location.pathname]);

  const safeTitle = (title ?? DEFAULT_TITLE).trim() || DEFAULT_TITLE;
  const safeDescription =
    (description ?? DEFAULT_DESCRIPTION).trim() || DEFAULT_DESCRIPTION;

  const safeKeywords = Array.isArray(keywords)
    ? keywords.join(", ")
    : keywords?.trim?.() || "";

  const safeOgTitle = (ogTitle || safeTitle).trim();
  const safeOgDescription = (ogDescription || safeDescription).trim();

  const websiteSchema = {
    "@context": "https://schema.org/",
    "@type": "WebSite",
    name: "Square Trigger",
    url: `${CANONICAL_ORIGIN}/`,
    potentialAction: {
      "@type": "SearchAction",
      target: `${CANONICAL_ORIGIN}/blogs?s={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Square Trigger",
    url: `${CANONICAL_ORIGIN}/`,
    logo: DEFAULT_OG_IMAGE,
    sameAs: [],
  };

  return (
    <Helmet>
      <title>{safeTitle}</title>

      <meta name="description" content={safeDescription} />
      {safeKeywords ? <meta name="keywords" content={safeKeywords} /> : null}

      <meta
        name="robots"
        content={noIndex ? "noindex, nofollow" : "index, follow"}
      />

      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:title" content={safeOgTitle} />
      <meta property="og:site_name" content={ogSiteName} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:description" content={safeOgDescription} />
      <meta property="og:type" content={ogType} />
      <meta property="og:image" content={ogImage} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={safeOgTitle} />
      <meta name="twitter:description" content={safeOgDescription} />
      <meta name="twitter:image" content={ogImage} />

      {location.pathname === "/" ? (
        <script type="application/ld+json">
          {JSON.stringify(websiteSchema)}
        </script>
      ) : null}

      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
    </Helmet>
  );
};

export default SEO;